import { watchImmediate } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
import { toBoolean, toJSON, toNumber, trimToNull, trimToUndefined } from "#ce/utils/props";
import { createDistanceFilterApi } from "#ce/shared/filters/distance/api/distance-filter";
import { t } from "#ce/shared/filters/i18n";
import {
  DEFAULT_LOCATION_PARAM,
  DISTANCE_PARAM,
  LAT_PARAM,
  LNG_PARAM,
  distanceFilterNames,
} from "#ce/shared/filters/distance/filter-names";

export const DEFAULT_DISTANCES = Object.freeze([5, 10, 15, 20, 25]);

/**
 * Normalizes the `distances` prop into an array of mile values. Custom-element
 * attributes arrive as strings, so a JSON array string (e.g. "[5,10,25]") is
 * parsed; anything that doesn't resolve to an array falls back to the default list.
 *
 * In select mode, a seeded distance (client default or URL value) missing from
 * the list is inserted in order so the select always has an option to display
 * for it. In slider mode the list is the fixed set of snap stops — a stray seed
 * snaps to the nearest stop instead (see seededDistance).
 */
function distancesFrom(props) {
  const parsed = toJSON(trimToUndefined(props.distances), true);
  const values = Array.isArray(parsed) ? [...parsed] : [...DEFAULT_DISTANCES];

  const seeded = Number.parseInt(trimToUndefined(props.distance));
  if (!isSliderControl(props) && Number.isInteger(seeded) && !values.includes(seeded)) {
    values.push(seeded);
    values.sort((a, b) => a - b);
  }

  return values;
}

function commonPlacesFrom(props) {
  const parsed = toJSON(trimToUndefined(props.commonPlaces), true);
  return Array.isArray(parsed) ? parsed : undefined;
}

function isSliderControl(props) {
  return trimToUndefined(props.distanceControl) === "slider";
}

/**
 * The `{ min, max }` miles of a slider that moves one mile at a time, or undefined when the
 * slider snaps through `distances` (no `distanceMax`) or the control is a select.
 */
function distanceRangeFrom(props) {
  const max = toNumber(trimToUndefined(props.distanceMax));

  if (!isSliderControl(props) || max === undefined) {
    return undefined;
  }

  return { min: toNumber(trimToUndefined(props.distanceMin), 1), max };
}

/** The whole miles in a plain decimal number, or NaN for anything else, such as "65abc". */
function wholeMiles(value) {
  const text = String(value).trim();

  return /^[-+]?\d+(\.\d+)?$/.test(text) ? Number.parseInt(text, 10) : Number.NaN;
}

export const useDistanceFilter = (props) => {
  const api = props.filterApi ?? createDistanceFilterApi(props);

  const geolocationSupported = computed(() => Boolean(navigator.geolocation));

  const searchQuery = ref("");

  const currentLocationId = "current-location";

  const loading = ref(false);
  const error = ref(null);

  const lat = ref(trimToUndefined(props.lat));
  const lng = ref(trimToUndefined(props.lng));

  const hasGeoCoordinates = computed(() => lat.value != null && lng.value != null);

  const selectedLocation = ref();
  const transientLocation = ref(false);
  let geolocationGeneration = 0;

  const distances = ref(distancesFrom(props));
  const distanceOptions = computed(() =>
    distances.value.map((value) => ({ value, title: t("distance_miles", { count: value }) })),
  );

  // Slider control (filter_options.distance.control: slider) replaces the option
  // select with an sp-number-slider.
  const sliderControl = computed(() => isSliderControl(props));
  const distanceRange = computed(() => distanceRangeFrom(props));

  const anyDistanceValue = computed(() => trimToUndefined(props.anyDistanceValue));

  function normalizeRequiredDistance(value) {
    if (toBoolean(props.allowAny) || (value != null && value !== "any" && value !== anyDistanceValue.value)) {
      return value;
    }

    const configured = trimToUndefined(props.distance);
    return Number.isFinite(Number(configured)) ? configured : distances.value[0];
  }

  // A seed on a slider that snaps through stops, still empty or non-numeric after required-radius
  // normalization, uses the largest stop, so the submitted radius matches what the slider displays.
  function seededDistance() {
    if (distanceRange.value) {
      return distanceInRange(trimToNull(props.distance));
    }

    const seeded = normalizeRequiredDistance(trimToNull(props.distance));

    if (!sliderControl.value) {
      return seeded;
    }

    const numeric = Number.parseInt(seeded);

    if (!distances.value.length) {
      return Number.isInteger(numeric) ? numeric : undefined;
    }

    if (Number.isInteger(numeric)) {
      return nearestStop(numeric);
    }

    // The configured list keeps its given order, so take the true maximum.
    return Math.max(...distances.value);
  }

  const selectedDistance = ref(seededDistance());

  // The distance select's prompt is its "Any Distance" (no-radius) option; a
  // falsy prompt drops that option, leaving only the concrete radii.
  const anyDistancePrompt = computed(() => (toBoolean(props.allowAny) ? t("distance_any") : undefined));

  const near = ref(trimToUndefined(props.near));

  const locationParam = computed(() => trimToUndefined(props.locationParam) ?? DEFAULT_LOCATION_PARAM);

  /** The params the model submits together, the location param first. */
  const filterNames = computed(() => distanceFilterNames(locationParam.value));

  // True while the seeded `near` or lat/lng is being resolved into a selection, so a model change
  // during that window is the mount's own value arriving rather than a pick.
  const applyingSeed = ref(false);

  const selectedDistancePrependText = computed(() => t("distance_within"));

  const currentLocationDisplayText = computed(
    () => trimToUndefined(props.currentLocationDisplayText) ?? t("current_location_display"),
  );
  const locationsGroupLabel = computed(
    () => trimToUndefined(props.locationsGroupLabel) ?? t("distance_popular_locations"),
  );

  const selectedDistanceText = computed(() => {
    const value = Number.parseInt(selectedDistance.value);

    if (distanceRange.value && Number.isInteger(value)) {
      return t("distance_miles", { count: value });
    }

    const option = distanceOptions.value.find((option) => option.value === value);
    return option?.title;
  });

  const isRetrievingCurrentLocation = ref(false);

  const configuredCommonPlaces = commonPlacesFrom(props);
  const commonPlaces = ref(configuredCommonPlaces ?? []);
  const placeSortOrder = ref([]);
  const googlePlaces = ref([]);

  /**
   * If the selected location changes, clear the geo coordinates if the location is different from the current location.
   * This is to ensure that the geo coordinates are cleared when the user selects a different location.
   *
   * Also mirror the selected location name into the search field so the user sees the chosen value in the input.
   */
  watchImmediate(selectedLocation, syncSelectedLocation);

  function syncSelectedLocation(location) {
    if (location && location.value !== currentLocationId) {
      clearGeoCoordinates();
    }
    if (!location) {
      searchQuery.value = "";
    } else if (location.value === currentLocationId) {
      searchQuery.value = currentLocationDisplayText.value;
    } else {
      searchQuery.value = location.name;
    }
  }

  // A seeded selection can arrive before the catalog; keep its copied search text in sync
  // without replacing a query the visitor has since typed.
  function syncCurrentLocationQuery(text, previous) {
    if (selectedLocation.value?.value === currentLocationId && searchQuery.value === previous) {
      searchQuery.value = text;
    }
  }

  watch(currentLocationDisplayText, syncCurrentLocationQuery);

  /**
   * Returns the locations to display in the dropdown.
   *
   * If there are google locations, they are returned grouped by "Search Results".
   * Otherwise, the popular locations are returned grouped by "Popular Locations".
   */
  const locations = computed(() =>
    googlePlaces.value.length > 0
      ? decoratePlaceModels(t("distance_search_results"), ...googlePlaces.value)
      : decoratePlaceModels(locationsGroupLabel.value, ...commonPlaces.value),
  );

  const model = computed(() => {
    const formData = new FormData();

    const nearValue = selectedLocation.value?.value;
    if (nearValue && nearValue !== currentLocationId) {
      formData.set(locationParam.value, nearValue);
    } else if (nearValue && toBoolean(props.submitCurrentLocation)) {
      formData.set(locationParam.value, selectedLocation.value.name);
    }

    // If the selected distance is set and the near value is set, we add the distance to the form data.
    // This is to ensure that the distance is only added when the near value is set.
    if (selectedDistance.value && nearValue) {
      formData.set(DISTANCE_PARAM, selectedDistance.value);
    }

    if (hasGeoCoordinates.value) {
      formData.set(LAT_PARAM, lat.value);
      formData.set(LNG_PARAM, lng.value);
    }

    return formData;
  });

  /**
   * Returns the selected location's activator text, or undefined without a selection.
   * Uses the translated current-location label for the current-location selection.
   * With a distance, formats both values through the translated distance summary;
   * otherwise, returns the location text alone.
   *
   * @returns {string | undefined}
   */
  const displayText = computed(() => {
    if (!selectedLocation.value) {
      return undefined;
    }

    const { name, value } = selectedLocation.value;
    const location = value === currentLocationId ? currentLocationDisplayText.value : name;

    if (!selectedDistanceText.value) {
      return location;
    }

    return t("distance_summary", {
      distance: selectedDistanceText.value,
      location,
    });
  });

  // Runs `apply` with `applyingSeed` held open through the model watcher's flush, which is where
  // the resulting emission reads it.
  async function applySeed(apply) {
    applyingSeed.value = true;
    apply();
    await nextTick();
    applyingSeed.value = false;
  }

  // Retires the seed, so a places request still in flight cannot reinstate it over what the visitor
  // did — `near` is the only thing `selectLocationIfAvailable` reads. Called from the two entry
  // points that change the selection on their behalf, rather than watched: a reset with nothing
  // selected writes `undefined` over `undefined`, which moves no ref and so fires no watcher.
  function retireSeed() {
    near.value = undefined;
  }

  async function initialize() {
    if (hasGeoCoordinates.value) {
      await applySeed(() => updateSelectedLocation(currentLocationId));
    }
    await Promise.all([loadCommonPlaces(), loadPlaceSortOrder()]);
  }

  // A failed request leaves the search results in the order Google returns them.
  async function loadPlaceSortOrder() {
    try {
      placeSortOrder.value = await api.getPlaceSortOrder();
    } catch {
      placeSortOrder.value = [];
    }
  }

  async function loadCommonPlaces() {
    if (configuredCommonPlaces) {
      await applySeed(selectLocationIfAvailable);
      return;
    }
    loading.value = true;

    try {
      const places = await api.getCommonPlaces();
      commonPlaces.value = places;
    } catch (e) {
      error.value = e;
    } finally {
      await applySeed(selectLocationIfAvailable);
      loading.value = false;
    }
  }

  function decoratePlaceModels(group, ...models) {
    const pinBackgroundColor = trimToUndefined(props.locationPinBackgroundColor);
    // A bare pin by default; when a background colour is configured, wrap the pin
    // in a circular badge of that colour (icon glyph keeps its fill token).
    const prependIcon = pinBackgroundColor
      ? { name: "map-pin-filled", background: { shape: "circle", color: pinBackgroundColor } }
      : "map-pin-filled";
    return decorateModels(
      {
        group,
        props: {
          prependIcon,
          prependIconProps: {
            fillColor: "var(--sp-comp-distance-filter-list-item-pin-fill-color, var(--sp-sys-color-primary))",
          },
        },
      },
      ...models,
    );
  }

  function decorateModels(options, ...models) {
    const { group, props = {} } = options;
    return models.map((model) => ({ ...model, group, props: { ...model.props, ...props } }));
  }

  function selectLocationIfAvailable() {
    // Nothing to seed without a `near`, or once geo coordinates supersede it. A selection made
    // while the places request was in flight is the visitor's, and the seed does not displace it —
    // that write reports as seeded, so the host would take it without re-running the search and
    // leave the results describing a location the filter no longer shows.
    if (!near.value || hasGeoCoordinates.value || selectedLocation.value) {
      return;
    }

    const location = findLocationByValue(near.value);

    if (location) {
      return updateSelectedLocation(location.value);
    }

    // If the location is not found, we assume it's a google place.
    // We update the search query and google result to display the location.
    searchQuery.value = near.value;

    const googlePlace = { value: near.value, name: near.value };
    updateGoogleResult([googlePlace]);
    selectedLocation.value = googlePlace;
  }

  function findLocationByValue(value) {
    return locations.value.find((location) => location.value === value);
  }

  /**
   * Determines if the current location should be retrieved.
   *
   * @param {String} locationValue
   * @returns {Boolean}
   */
  function shouldRetrieveCurrentLocation(locationValue) {
    return locationValue === currentLocationId && !hasGeoCoordinates.value;
  }

  function updateSelectedLocation(value, options) {
    if (!applyingSeed.value) {
      retireSeed();
    }

    if (shouldRetrieveCurrentLocation(value)) {
      return retrieveCurrentLocation(options);
    } else if (value === currentLocationId) {
      selectedLocation.value = { value: currentLocationId, name: currentLocationDisplayText.value };
    } else {
      invalidateGeolocation();
      if (!applyingSeed.value) {
        transientLocation.value = false;
      }
      const location = findLocationByValue(value);
      selectedLocation.value = location ? { ...location } : undefined;
    }
  }

  function selectCurrentLocation(options) {
    if (isRetrievingCurrentLocation.value) {
      return;
    }
    return updateSelectedLocation(currentLocationId, options);
  }

  function unsetSelectedLocation() {
    invalidateGeolocation();
    retireSeed();
    selectedLocation.value = undefined;
  }

  function invalidateGeolocation() {
    geolocationGeneration += 1;
    isRetrievingCurrentLocation.value = false;
  }

  /**
   * Retrieves the current location.
   * If the location retrieval is successful, the geo coordinates are updated.
   * Otherwise, the existing selection is retained and an error is shown.
   * With a `timeout`, a request still unanswered after that many milliseconds resolves false without an error.
   */
  function retrieveCurrentLocation({ notify = true, timeout } = {}) {
    const generation = ++geolocationGeneration;
    isRetrievingCurrentLocation.value = true;
    return new Promise((resolve) => {
      const timer = Number.isFinite(timeout) ? setTimeout(handleCurrentLocationTimeout, timeout) : undefined;

      function handleCurrentLocationTimeout() {
        if (generation === geolocationGeneration) {
          invalidateGeolocation();
        }
        resolve(false);
      }

      function handleCurrentLocationSuccess({ coords }) {
        clearTimeout(timer);
        if (generation !== geolocationGeneration) {
          resolve(false);
          return;
        }
        isRetrievingCurrentLocation.value = false;
        if (toBoolean(props.disabled)) {
          resolve(false);
          return;
        }
        transientLocation.value = true;
        updateGeoCoordinates(coords);
        updateSelectedLocation(currentLocationId);
        resolve(true);
      }

      function handleCurrentLocationFailure() {
        clearTimeout(timer);
        if (generation !== geolocationGeneration) {
          resolve(false);
          return;
        }
        isRetrievingCurrentLocation.value = false;
        if (toBoolean(props.disabled)) {
          resolve(false);
          return;
        }
        if (notify) {
          showCurrentLocationRetrievalError();
        }
        resolve(false);
      }

      navigator.geolocation.getCurrentPosition(handleCurrentLocationSuccess, handleCurrentLocationFailure);
    });
  }

  async function selectRemoteLocation() {
    const generation = ++geolocationGeneration;
    try {
      const place = await api.getRemoteLocation();
      if (generation !== geolocationGeneration || toBoolean(props.disabled) || !place?.city) {
        return false;
      }
      retireSeed();
      clearGeoCoordinates();
      transientLocation.value = true;
      selectedLocation.value = { value: place.city, name: place.city, selectedText: place.address || place.city };
      return true;
    } catch {
      return false;
    }
  }

  function showCurrentLocationRetrievalError() {
    // eslint-disable-next-line no-restricted-syntax
    window.alert(trimToUndefined(props.currentLocationRetrievalErrorText) ?? t("current_location_error"));
  }

  function updateGeoCoordinates({ latitude, longitude }) {
    lat.value = roundCoordinate(latitude);
    lng.value = roundCoordinate(longitude);
  }

  function roundCoordinate(coordinate) {
    const precision = Number.parseInt(trimToUndefined(props.coordinatePrecision));

    return Number.isInteger(precision) && precision >= 0 && precision <= 100
      ? Number.parseFloat(Number(coordinate).toFixed(precision))
      : coordinate;
  }

  function clearGeoCoordinates() {
    lat.value = undefined;
    lng.value = undefined;
  }

  /**
   * Updates the google result.
   *
   * If the result is an array, it updates the google locations. Otherwise it
   * clears the google results without touching the current selection — an
   * empty result can come from a non-OK Places status (ZERO_RESULTS, OVER_QUERY_LIMIT,
   * etc.) and must not silently clear the user's chosen location. Use
   * `handleSearchInput` for explicit user-driven clearing.
   *
   * @param {Array<Object>|undefined} result
   */
  function updateGoogleResult(result) {
    googlePlaces.value = [];

    if (Array.isArray(result) && result.length > 0) {
      googlePlaces.value = result;
    }
  }

  /**
   * Reacts to the user typing into (or clearing) the search input. Only an
   * explicit empty input — i.e. the user clearing the field — should drop the
   * current selection; intermediate keystrokes leave the selection alone so
   * the user can refine their query without losing context.
   *
   * @param {String} query
   */
  function handleSearchInput(query) {
    searchQuery.value = query;
    if (query === "" && selectedLocation.value && !toBoolean(props.nonClearable)) {
      clear();
    }
  }

  /**
   * Restores the selection from search params keyed by `filterNames`. A key that is absent leaves
   * its part of the selection alone, and a blank value clears it. A complete `lat`/`lng` pair
   * selects the current location, and otherwise the location param is resolved the way a seeded
   * `near` is: against the places list once it has loaded.
   *
   * @param {Object} [values]
   */
  async function setFilterValues(values = {}, metadata = {}) {
    if (locationParam.value in values || LAT_PARAM in values || LNG_PARAM in values) {
      invalidateGeolocation();
    }

    if (DISTANCE_PARAM in values) {
      selectedDistance.value = restoredDistance(values[DISTANCE_PARAM]);
    }

    const restoresCoordinates = LAT_PARAM in values || LNG_PARAM in values;
    const retainsCoordinates =
      String(trimToUndefined(values[LAT_PARAM])) === String(lat.value) &&
      String(trimToUndefined(values[LNG_PARAM])) === String(lng.value);
    if (LAT_PARAM in values) {
      lat.value = trimToUndefined(values[LAT_PARAM]);
    }
    if (LNG_PARAM in values) {
      lng.value = trimToUndefined(values[LNG_PARAM]);
    }

    if (restoresCoordinates && hasGeoCoordinates.value) {
      if (typeof metadata.transientLocation === "boolean") {
        transientLocation.value = metadata.transientLocation;
      } else if (!retainsCoordinates) {
        transientLocation.value = false;
      }
      await applySeed(() => updateSelectedLocation(currentLocationId));

      return;
    }

    if (!(locationParam.value in values)) {
      return;
    }

    const restoredNear = trimToUndefined(values[locationParam.value]);

    if (
      restoredNear &&
      (restoredNear === selectedLocation.value?.value || restoredNear === model.value.get(locationParam.value))
    ) {
      if (typeof metadata.transientLocation === "boolean") {
        transientLocation.value = metadata.transientLocation;
      }
      return;
    }

    transientLocation.value = Boolean(restoredNear && metadata.transientLocation === true);
    clearGeoCoordinates();
    selectedLocation.value = undefined;
    near.value = restoredNear;

    if (!loading.value) {
      await applySeed(selectLocationIfAvailable);
    }
  }

  // The slider offers only its range or stops, so a restored distance clamps or snaps the way a
  // seeded one does.
  function restoredDistance(value) {
    const restored = trimToUndefined(value);

    if (distanceRange.value && (restored != null || !toBoolean(props.allowAny))) {
      return distanceInRange(restored);
    }

    const distance = normalizeRequiredDistance(restored);
    if (distance == null) {
      return undefined;
    }
    const numeric = Number.parseInt(distance);

    if (!sliderControl.value && Number.isInteger(numeric) && !distances.value.includes(numeric)) {
      distances.value = [...distances.value, numeric].sort((a, b) => a - b);
    }

    if (!sliderControl.value || !distances.value.length) {
      return distance;
    }

    return Number.isInteger(numeric) ? nearestStop(numeric) : Math.max(...distances.value);
  }

  function nearestStop(numeric) {
    return distances.value.reduce((best, stop) => (Math.abs(stop - numeric) < Math.abs(best - numeric) ? stop : best));
  }

  function clampToRange(miles) {
    const { min, max } = distanceRange.value;

    return Math.min(max, Math.max(min, miles));
  }

  // The radius a range uses for a URL or stored value. A number is cut to whole miles and clamped,
  // "any" is the largest radius, and anything else is the `distance` prop, or the largest radius
  // when that isn't a number. On a required range, `ProviderSearch::Base` resolves `distance` the
  // same way.
  function distanceInRange(value) {
    const numeric = wholeMiles(value);

    if (!Number.isNaN(numeric)) {
      return clampToRange(numeric);
    }

    const configured = wholeMiles(props.distance);

    if (String(value).toLowerCase() === "any" || Number.isNaN(configured)) {
      return distanceRange.value.max;
    }

    return clampToRange(configured);
  }

  // The select's "Any Distance" prompt reports no value. Where a search names that choice, it is
  // submitted as `anyDistanceValue`.
  function selectDistance(distance) {
    selectedDistance.value = normalizeRequiredDistance(trimToUndefined(distance) ?? anyDistanceValue.value);
  }

  const pickedDistance = computed(() =>
    selectedDistance.value === anyDistanceValue.value ? undefined : selectedDistance.value,
  );

  function clear() {
    transientLocation.value = false;
    unsetSelectedLocation();
    selectedDistance.value =
      toBoolean(props.allowAny) && !toBoolean(props.preserveDistanceOnClear) ? undefined : seededDistance();
    googlePlaces.value = [];

    clearGeoCoordinates();
  }
  return {
    // state
    loading,
    error,
    locations,
    model,
    filterNames,
    selectedLocation,
    distanceOptions,
    anyDistancePrompt,
    selectedDistance,
    pickedDistance,
    selectedDistanceText,
    selectedDistancePrependText,
    sliderControl,
    distanceRange,
    distances,
    placeSortOrder,
    displayText,
    searchQuery,
    applyingSeed,
    transientLocation,
    geolocationSupported,
    isRetrievingCurrentLocation,

    // actions
    initialize,
    findLocationByValue,
    updateGoogleResult,
    handleSearchInput,
    updateSelectedLocation,
    updateGeoCoordinates,
    selectCurrentLocation,
    selectRemoteLocation,
    cancelLocationLookup: invalidateGeolocation,
    selectDistance,
    setFilterValues,
    clear,
  };
};
